import React from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Car, Edit, Trash2, Eye } from "lucide-react";
import { VehicleModel } from "@/types";

interface VehicleModelCardProps {
  vehicleModel: VehicleModel;
  onEdit?: (vehicleModel: VehicleModel) => void;
  onDelete?: (vehicleModel: VehicleModel) => void;
  onView?: (vehicleModel: VehicleModel) => void;
}

export const VehicleModelCard: React.FC<VehicleModelCardProps> = ({
  vehicleModel,
  onEdit,
  onDelete,
  onView,
}) => {
  const { t } = useTranslation(["pages", "common"]);

  const formatFullName = (maker: string, model: string, year: number) => {
    return `${year} ${maker} ${model}`;
  };

  return (
 <div className="bg-white rounded-lg border border-secondary-200 p-6">
 <div className="flex justify-between items-start mb-4">
 <div className="flex-1">
          <h3
 className="text-xl font-semibold text-secondary-900 mb-2 cursor-pointer hover:text-primary-600 transition-colors"
            onClick={() => onView?.(vehicleModel)}
          >
            {formatFullName(
              vehicleModel.maker,
              vehicleModel.model,
              vehicleModel.year,
            )}
          </h3>
 <div className="flex items-center gap-4 mb-2">
 <div className="flex items-center gap-2">
 <Car className="h-4 w-4 text-secondary-500" />
 <span className="text-sm text-secondary-600">
                {vehicleModel.maker}
              </span>
            </div>
 <div className="flex items-center gap-2">
 <Calendar className="h-4 w-4 text-secondary-500" />
 <span className="text-sm text-secondary-600">{vehicleModel.year}</span>
            </div>
          </div>
 <div className="mb-2">
 <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs font-medium">
              {vehicleModel.model}
            </span>
          </div>
        </div>
      </div>

      {vehicleModel.description && (
 <div className="mb-4">
  <p className="text-sm text-secondary-600 mb-1">{t("pages:vehicles.models.cardDescription")}</p>
 <p className="text-secondary-800 text-sm line-clamp-2">
            {vehicleModel.description}
          </p>
        </div>
      )}

 <div className="flex justify-between items-center">
 <div className="text-sm text-secondary-500">
          {t("pages:vehicles.models.cardAdded", {
            date: new Date(vehicleModel.createdAt).toLocaleDateString(),
          })}
        </div>
 <div className="flex gap-2">
          {onView && (
            <button
              onClick={() => onView(vehicleModel)}
               className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
               title={t("pages:vehicles.models.cardViewTitle")}
               aria-label={t("pages:vehicles.models.cardViewAria")}
            >
 <Eye className="h-4 w-4" />
            </button>
          )}
          {onEdit && (
            <button
              onClick={() => onEdit(vehicleModel)}
               className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-lg transition-colors"
               title={t("pages:vehicles.models.cardEditTitle")}
               aria-label={t("pages:vehicles.models.cardEditAria")}
            >
 <Edit className="h-4 w-4" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(vehicleModel)}
               className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
               title={t("pages:vehicles.models.cardDeleteTitle")}
               aria-label={t("pages:vehicles.models.cardDeleteAria")}
            >
 <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
